// Поставщик высоты экранной клавиатуры (перенос из AssistentusApp, упрощён:
// без мини-приложения Телеграма, оно в Saleus не живёт).
//
// Источников высоты несколько, и ни одного не хватает на всех платформах:
//   1. нативные события оболочки — приходят ДО движения, значение точное;
//   2. системный интерфейс браузера (virtualKeyboard) — Хром/Андроид;
//   3. видимое окно (visualViewport) — единственное, что работает на iOS в браузере;
//   4. предсказание по фокусу — подставляем прошлое значение, не дожидаясь событий.
//
// 🔴 ДВА ВОПРОСА, А НЕ ОДИН (24.09.2026, урок Ассистентуса). «Открыта ли
// клавиатура» и «на сколько поднять нижние панели» — разные вещи: окно страницы
// может сжаться само, и тогда перекрытие равно нулю. Поэтому:
//   • «открыта»  — класс kb-open на корне: прячет нижнее меню;
//   • «перекрытие» — переменная --kb-h: сколько клавиатура ЗАКРЫВАЕТ снизу.
// Величина пишется ПРЯМО в стиль корня, минуя React: множественные
// перерисовки дают мигание нижних панелей.

/** Меньше этого — не клавиатура, а адресная строка. */
const ПОРОГ_ШУМА_ТОЧЕК = 80;

let установлено = false;
let последнееПерекрытие = 0;
let базоваяВысота = typeof window !== 'undefined' ? window.innerHeight : 0;

interface ВиртуальнаяКлавиатура extends EventTarget {
  overlaysContent: boolean;
  boundingRect?: { height: number };
}

function применить(открыта: boolean, перекрытие: number): void {
  const корень = document.documentElement;
  const лифт = открыта && перекрытие >= ПОРОГ_ШУМА_ТОЧЕК ? Math.round(перекрытие) : 0;
  корень.style.setProperty('--kb-h', `${лифт}px`);
  if (открыта) {
    последнееПерекрытие = лифт;
    корень.classList.add('kb-open');
  } else {
    корень.classList.remove('kb-open');
  }
}

function естьФокусВПоле(узел: Element | null): boolean {
  if (!узел) return false;
  const имя = узел.tagName;
  if (имя === 'INPUT' || имя === 'TEXTAREA' || имя === 'SELECT') return true;
  return !!(узел as HTMLElement).isContentEditable;
}

/** Сбой iOS 26: после скрытия клавиатуры смещение окна не сбрасывается. */
function поправитьЗастрявшееОкно(): void {
  const вв = window.visualViewport;
  if (!вв || вв.offsetTop === 0) return;
  if (естьФокусВПоле(document.activeElement)) return;
  window.scrollTo(0, 0);
  измерить();
}

function измерить(): void {
  const вв = window.visualViewport;
  const фокус = естьФокусВПоле(document.activeElement);
  if (!фокус) базоваяВысота = Math.max(базоваяВысота, window.innerHeight);
  if (!вв) { применить(false, 0); return; }
  const перекрытие = Math.max(0, window.innerHeight - вв.height - вв.offsetTop);
  const сжатие = Math.max(0, базоваяВысота - вв.height);
  const открыта = фокус && Math.max(перекрытие, сжатие) >= ПОРОГ_ШУМА_ТОЧЕК;
  применить(открыта, перекрытие);
}

function поставитьСистемныйИнтерфейс(): boolean {
  const вк = (navigator as unknown as { virtualKeyboard?: ВиртуальнаяКлавиатура }).virtualKeyboard;
  if (!вк) return false;
  try {
    // Просим браузер НЕ сжимать разметку самому: геометрию считаем мы.
    вк.overlaysContent = true;
    вк.addEventListener('geometrychange', () => {
      const высота = вк.boundingRect?.height ?? 0;
      применить(высота >= ПОРОГ_ШУМА_ТОЧЕК, высота);
    });
    return true;
  } catch {
    return false;
  }
}

function поставитьВидимоеОкно(): void {
  const вв = window.visualViewport;
  if (!вв) return;
  вв.addEventListener('resize', измерить);
  вв.addEventListener('scroll', измерить);
  window.addEventListener('resize', измерить);
  window.addEventListener('orientationchange', () => {
    базоваяВысота = 0; // поворот меняет «обычную» высоту
    setTimeout(измерить, 350);
  });
  измерить();
}

function поставитьПредсказание(нативно: boolean): void {
  document.addEventListener('focusin', (событие) => {
    if (!естьФокусВПоле(событие.target as Element)) return;
    if (!нативно) применить(true, последнееПерекрытие);
    setTimeout(измерить, 350);
  });
  document.addEventListener('focusout', () => {
    // Перевод фокуса между полями клавиатуру не убирает — даём браузеру мгновение.
    setTimeout(() => {
      if (естьФокусВПоле(document.activeElement)) return;
      if (!нативно) измерить();
      поправитьЗастрявшееОкно();
    }, 120);
  });
}

/** Убрать клавиатуру — одна дверь на всё приложение. */
export async function спрятатьКлавиатуру(): Promise<void> {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      const { Keyboard } = await import('@capacitor/keyboard');
      await Keyboard.hide();
    }
  } catch { /* не оболочка телефона */ }
  const фокус = document.activeElement as HTMLElement | null;
  if (фокус && естьФокусВПоле(фокус)) фокус.blur();
}

export async function installKeyboardSync(): Promise<void> {
  if (установлено) return;
  установлено = true;
  применить(false, 0);

  try {
    // Ленивый ввоз: веб-сборка работает и без нативного пакета.
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      const { Keyboard } = await import('@capacitor/keyboard');
      // Открыта/закрыта — только по нативным событиям: угадывание по фокусу
      // здесь врало (кнопка забирала фокус, а клавиатура оставалась).
      void Keyboard.addListener('keyboardWillShow', (сведения) => {
        применить(true, сведения?.keyboardHeight ?? 0);
      });
      void Keyboard.addListener('keyboardWillHide', () => применить(false, 0));
      поставитьПредсказание(true);
      return;
    }
  } catch { /* пакета нет — обычный браузер */ }

  if (!поставитьСистемныйИнтерфейс()) поставитьВидимоеОкно();
  поставитьПредсказание(false);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) поправитьЗастрявшееОкно();
  });
}
